import { api } from "../../../api/apiSlice";

// Branch-wise master (handoff §9.7) - list/search is automatically scoped
// to the caller's own branch(es) server-side. The underlying table has
// 5000+ rows across all branches, so the list screen always passes a
// search/isActive filter rather than fetching everything unfiltered.
//
// custId is only branch-local, not globally unique (1,297 of 3,695 collide
// across branches - the legacy sync pulls from three separate branch DBs
// whose local counters overlap) - every single-customer route needs
// {branchId} alongside {custId} to disambiguate.
export const customersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCustomers: builder.query({
      query: (params) => ({ url: "/masters/customers", params }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((c) => ({ type: "MasterCustomers", id: c.custId })),
              { type: "MasterCustomers", id: "LIST" },
            ]
          : [{ type: "MasterCustomers", id: "LIST" }],
    }),
    getCustomer: builder.query({
      query: ({ branchId, custId }) => ({ url: `/masters/customers/${branchId}/${custId}` }),
      providesTags: (result, error, { custId }) => [{ type: "MasterCustomers", id: custId }],
    }),
    createCustomer: builder.mutation({
      query: (body) => ({ url: "/masters/customers", method: "POST", data: body }),
      invalidatesTags: [{ type: "MasterCustomers", id: "LIST" }],
    }),
    updateCustomer: builder.mutation({
      query: ({ branchId, custId, ...body }) => ({
        url: `/masters/customers/${branchId}/${custId}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: (result, error, { custId }) => [
        { type: "MasterCustomers", id: custId },
        { type: "MasterCustomers", id: "LIST" },
      ],
    }),
    // Full-list diff pattern, same as Deductions' slab rows. assignmentId is
    // never sent by the client - always server-generated. Multi-location
    // customers (handoff §19.6/§9.7 Guard Assignments update, added
    // 2026-08-05): pass `locationId` to scope the replace to one specific
    // site's assignments only (verified live - a location-scoped PUT never
    // touches another location's rows, including when locationId is the
    // customer's own primary location). Omitting locationId falls back to
    // the original customer-level route, which still works unchanged and
    // resolves to the primary location automatically - kept as a fallback,
    // though every caller in this app now always passes locationId.
    replaceCustomerAssignments: builder.mutation({
      query: ({ branchId, custId, locationId, assignments }) => ({
        url: locationId
          ? `/masters/customers/${branchId}/${custId}/locations/${locationId}/assignments`
          : `/masters/customers/${branchId}/${custId}/assignments`,
        method: "PUT",
        data: { assignments },
      }),
      invalidatesTags: (result, error, { custId }) => [
        { type: "MasterCustomers", id: custId },
      ],
    }),
    // Activate/deactivate ONE assignment with an effective/temp-end date -
    // replaces (not appends to) that assignment's single current status row.
    setAssignmentStatus: builder.mutation({
      query: ({ branchId, custId, assignmentId, ...body }) => ({
        url: `/masters/customers/${branchId}/${custId}/assignments/${assignmentId}/status`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (result, error, { custId }) => [
        { type: "MasterCustomers", id: custId },
      ],
    }),
    // Server-side bulk-create of N guarding posts, continuing the
    // customer's own assignmentId sequence. Same optional locationId
    // scoping as replaceCustomerAssignments above.
    bulkCreateAssignments: builder.mutation({
      query: ({ branchId, custId, locationId, ...body }) => ({
        url: locationId
          ? `/masters/customers/${branchId}/${custId}/locations/${locationId}/assignments/bulk`
          : `/masters/customers/${branchId}/${custId}/assignments/bulk`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (result, error, { custId }) => [
        { type: "MasterCustomers", id: custId },
      ],
    }),
    // Cross-branch linking (handoff §9.7/§13.2) - each branch keeps its own
    // fully independent customer record; linking only adds an identity-level
    // cross-reference so staff can see "this is also a customer in Nairobi".
    // Never touches guard assignments or any other operational data.
    searchCustomersAllBranches: builder.query({
      query: (searchQuery) => ({
        url: "/masters/customers/search-all-branches",
        params: { query: searchQuery },
      }),
    }),
    getLinkedCustomers: builder.query({
      query: ({ branchId, custId }) => ({
        url: `/masters/customers/${branchId}/${custId}/linked`,
      }),
      providesTags: (result, error, { custId }) => [
        { type: "MasterCustomerLinks", id: custId },
      ],
    }),
    linkCustomer: builder.mutation({
      query: ({ branchId, custId, targetBranchId, targetCustId }) => ({
        url: `/masters/customers/${branchId}/${custId}/link`,
        method: "POST",
        data: { targetBranchId, targetCustId },
      }),
      invalidatesTags: (result, error, { custId }) => [
        { type: "MasterCustomerLinks", id: custId },
      ],
    }),
    unlinkCustomer: builder.mutation({
      query: ({ branchId, custId }) => ({
        url: `/masters/customers/${branchId}/${custId}/link`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { custId }) => [
        { type: "MasterCustomerLinks", id: custId },
      ],
    }),
    // Branch mapping (handoff §13.5) - a different, additional capability
    // from linking above: the customer stays ONE row at its home branchId,
    // this just marks which OTHER branches can also see/use it (no second
    // row created). Full-desired-list diff, same convention as guard
    // assignments - send the whole set of target branchIds each time.
    getCustomerBranchMap: builder.query({
      query: ({ branchId, custId }) => ({
        url: `/masters/customers/${branchId}/${custId}/branch-map`,
      }),
      providesTags: (result, error, { custId }) => [
        { type: "MasterCustomerBranchMap", id: custId },
      ],
    }),
    setCustomerBranchMap: builder.mutation({
      query: ({ branchId, custId, branchIds }) => ({
        url: `/masters/customers/${branchId}/${custId}/branch-map`,
        method: "PUT",
        data: { branchIds },
      }),
      invalidatesTags: (result, error, { custId }) => [
        { type: "MasterCustomerBranchMap", id: custId },
        { type: "MasterCustomers", id: "LIST" },
      ],
    }),
    // QR codes + field-location tracking (handoff §19) - two additions to
    // the existing Customers module, not a new one. Latitude/Longitude are
    // read-only via the web everywhere - only a Flutter QR scan writes them.
    //
    // Lazy-generated PNG (not JSON) - image/png, requires only
    // Customers.View. Only the raw opaque token is encoded, never a URL, so
    // a printed sticker reveals nothing to a random phone's camera app.
    getCustomerQrCode: builder.query({
      query: ({ branchId, custId }) => ({
        url: `/masters/customers/${branchId}/${custId}/qr`,
        responseType: "blob",
      }),
    }),
    // Every scan event, newest first (who/where/deviceCapturedAt vs
    // receivedAt) - the full audit trail behind comparing the current
    // customer_master pin against where someone actually scanned.
    // Customers.ViewLocation, admin/manager-only by default.
    getFieldVisits: builder.query({
      query: ({ branchId, custId, dateFrom, dateTo }) => ({
        url: `/masters/customers/${branchId}/${custId}/field-visits`,
        params: { dateFrom, dateTo },
      }),
    }),
    // Global field-visits list (§19.3.1, added 2026-08-05) - no per-customer
    // scope required, unlike getFieldVisits above. Powers the combined
    // admin map+activity dashboard: filter by branchId/custId/empCode/
    // scannedByUserId/date range without looping per-customer calls. Each
    // row also carries scannedByEmployeeCode/scannedByEmployeeName
    // (resolved via App_Users.EmployeeId -> Employee_Details) alongside the
    // plain scannedByUserId/scannedByName (the logged-in account's name) -
    // only populated where that account is actually linked to an employee.
    getFieldVisitsGlobal: builder.query({
      query: (params) => ({ url: "/masters/customers/field-visits", params }),
    }),
    // Every customer with a known location, for the admin map screen.
    // Customers.ViewLocation. Same &branchId= narrowing as every other list
    // endpoint (§13.1); omit for every branch the caller can see.
    getCustomerLocations: builder.query({
      query: (params) => ({ url: "/masters/customers/locations", params }),
    }),
    // Multi-location customers (handoff §19.6, added 2026-08-05) - a
    // customer can have several distinct physical sites (house/office/etc),
    // each with its own guard assignments and QR/scan tracking. One
    // customer_master row per real person, always - these are child rows,
    // same "one parent, many children" shape as guard assignments. Same
    // Customers.View/.Edit permissions as everything else on this
    // controller, no new permission. All three write endpoints return the
    // full customer detail (customer/assignments/locations), same shape as
    // getCustomer - invalidating MasterCustomers is enough to refresh
    // everywhere via the existing getCustomer subscription.
    createCustomerLocation: builder.mutation({
      query: ({ branchId, custId, ...body }) => ({
        url: `/masters/customers/${branchId}/${custId}/locations`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (result, error, { custId }) => [{ type: "MasterCustomers", id: custId }],
    }),
    updateCustomerLocation: builder.mutation({
      query: ({ branchId, custId, locationId, ...body }) => ({
        url: `/masters/customers/${branchId}/${custId}/locations/${locationId}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: (result, error, { custId }) => [{ type: "MasterCustomers", id: custId }],
    }),
    deleteCustomerLocation: builder.mutation({
      query: ({ branchId, custId, locationId }) => ({
        url: `/masters/customers/${branchId}/${custId}/locations/${locationId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { custId }) => [{ type: "MasterCustomers", id: custId }],
    }),
    // Per-location QR (distinct from the customer-level GET .../qr, which
    // always mirrors the PRIMARY location only) - image/png via Blob, same
    // pattern as getCustomerQrCode.
    getCustomerLocationQrCode: builder.query({
      query: ({ branchId, custId, locationId }) => ({
        url: `/masters/customers/${branchId}/${custId}/locations/${locationId}/qr`,
        responseType: "blob",
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCustomersQuery,
  useLazyGetCustomersQuery,
  useGetCustomerQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useReplaceCustomerAssignmentsMutation,
  useSetAssignmentStatusMutation,
  useBulkCreateAssignmentsMutation,
  useLazySearchCustomersAllBranchesQuery,
  useGetLinkedCustomersQuery,
  useLinkCustomerMutation,
  useUnlinkCustomerMutation,
  useGetCustomerBranchMapQuery,
  useSetCustomerBranchMapMutation,
  useGetCustomerQrCodeQuery,
  useGetFieldVisitsQuery,
  useGetFieldVisitsGlobalQuery,
  useGetCustomerLocationsQuery,
  useCreateCustomerLocationMutation,
  useUpdateCustomerLocationMutation,
  useDeleteCustomerLocationMutation,
  useLazyGetCustomerLocationQrCodeQuery,
} = customersApi;
